import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Stage, Layer, Rect, Image as KonvaImage, Group, Circle as KonvaCircle, Line } from 'react-konva';
import styled from 'styled-components';
import { Annotation, ImageData, DamageType, damageDescriptions, AnnotationShape } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { ZoomIn, ZoomOut, Move, MousePointer, Square, Circle as CircleIcon, PenTool, Save, Trash2, ChevronLeft, ChevronRight, Wand2, Database } from 'lucide-react';
import { captureAnnotationArea, analyzeBladeDamage } from '../utils/aiUtils';
import { inspectionService } from '../services/inspectionService';
import { InspectionRecord } from '../types';

// --- Styled Components ---

const MainContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background-color: #f5f7fa;
`;

const ToolbarContainer = styled.div`
  width: 60px;
  background: white;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0.5rem;
  gap: 1rem;
  z-index: 10;
  box-shadow: 2px 0 5px rgba(0,0,0,0.02);
`;

const ToolGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ToolButton = styled.button<{ active?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: ${props => props.active ? '1px solid #3b82f6' : '1px solid transparent'};
  background-color: ${props => props.active ? '#eff6ff' : 'transparent'};
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? '#eff6ff' : '#f3f4f6'};
    color: ${props => props.active ? '#3b82f6' : '#374151'};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const CanvasArea = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e5e7eb; // Neutral background for canvas area
`;

const CanvasWrapper = styled.div`
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  background: white;
`;

const RightPanel = styled.div`
  width: 320px;
  background: white;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0,0,0,0.02);
`;

const PanelHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  
  h2 {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }
  
  p {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0.25rem 0 0 0;
  }
`;

const PanelContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  
  span:first-child {
    color: #6b7280;
    font-weight: 500;
  }
  
  span:last-child {
    color: #111827;
    font-weight: 600;
  }
`;

const AnnotationCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
  background: #f9fafb;
`;

const NavigationBar = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 20;
`;

const NavButton = styled.button`
  background: transparent;
  border: none;
  color: #374151;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 50%;
  
  &:hover {
    background-color: #f3f4f6;
  }
  
  &:disabled {
    color: #d1d5db;
    cursor: not-allowed;
  }
`;

const NavText = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  min-width: 80px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #fee2e2;
  color: #991b1b;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #fecaca;
  text-align: center;
`;

// --- Interfaces ---

interface ImageAnnotatorProps {
  images: ImageData[];
  annotations: Annotation[];
  onAddAnnotation: (annotation: Annotation) => void;
  onDeleteAnnotation: (annotationId: string) => void;
  onUpdateAnnotation: (annotation: Annotation) => void;
  selectedBlade: string | null;
  selectedSurface: string | null;
  currentImageIndex: number;
  setCurrentImageIndex: (index: number) => void;
  projectId?: string; // Add project ID for inspection saving
}

// --- Component ---

const ImageAnnotator: React.FC<ImageAnnotatorProps> = ({
  images,
  annotations,
  onAddAnnotation,
  onDeleteAnnotation,
  onUpdateAnnotation,
  selectedBlade,
  selectedSurface,
  currentImageIndex,
  setCurrentImageIndex,
  projectId, // Get project ID from parent
}) => {
  // State
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<'move' | 'rect' | 'circle' | 'polygon'>('move');
  const [imgObj, setImgObj] = useState<HTMLImageElement | null>(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [newAnnotation, setNewAnnotation] = useState<Partial<Annotation> | null>(null);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);

  // HD Mode State
  const [isHD, setIsHD] = useState(false);
  const [hdUrl, setHdUrl] = useState<string | null>(null);

  // Refs
  const stageRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageLayerRef = useRef<any>(null);

  // Filtered images
  // Filtered and sorted images
  const filteredImages = React.useMemo(() => {
    const output = images.filter(img =>
      (!selectedBlade || img.bladeName === selectedBlade) &&
      (!selectedSurface || img.surfaceType === selectedSurface)
    );

    // Altitude'a göre sırala (yoksa isme göre)
    return output.sort((a, b) => {
      if (a.altitude !== undefined && b.altitude !== undefined) {
        return a.altitude - b.altitude;
      }
      return a.fileName.localeCompare(b.fileName, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [images, selectedBlade, selectedSurface]);

  const currentImage = filteredImages[currentImageIndex];

  // Calculate altitude offset (first image = 0m reference)
  const referenceAltitude = filteredImages[0]?.altitude;
  const currentAltitude = currentImage?.altitude;
  const altitudeOffset = (referenceAltitude !== undefined && currentAltitude !== undefined)
    ? Math.round(currentAltitude - referenceAltitude)
    : null;

  // Filter annotations for current image
  const currentAnnotations = currentImage
    ? annotations.filter(a => a.imageData.path === currentImage.path)
    : [];

  // Reset state when image changes
  useEffect(() => {
    setImgObj(null);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setError(null);
    setIsHD(false);
    setHdUrl(null);
  }, [currentImage]);

  // Load Image with FORCED Preview Mode (Never auto-load HD)
  useEffect(() => {
    if (!currentImage) return;

    let isMounted = true;
    setIsLoading(true);
    setError(null);
    setImgObj(null); // Clear previous image immediately

    const loadSmartImage = async () => {
      try {
        let srcToLoad = '';

        // 1. If local blob (creating new inspection), use immediately
        if (currentImage.url && currentImage.url.startsWith('blob:')) {
          srcToLoad = currentImage.url;
        }
        // 2. If remote URL (R2), ALWAYS use preview
        else if (currentImage.url && currentImage.url.startsWith('http')) {
          try {
            const { getImageFromDB, saveImageToDB } = await import('../utils/zipUtils');
            // Check Cache
            const previewBlob = await getImageFromDB(currentImage.path + '_preview');

            if (previewBlob) {
              console.log('[SmartCache] Found cached preview:', currentImage.path);
              srcToLoad = URL.createObjectURL(previewBlob);
            } else {
              console.log('[SmartCache] Creating preview for:', currentImage.path);
              // Download Original
              const resp = await fetch(currentImage.url);
              if (!resp.ok) throw new Error('Failed to fetch image');
              const blob = await resp.blob();

              // Resize to preview
              const resizedBlob = await new Promise<Blob>((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                  const canvas = document.createElement('canvas');
                  let width = img.width;
                  let height = img.height;
                  const MAX_SIZE = 1500;

                  if (width > MAX_SIZE || height > MAX_SIZE) {
                    if (width > height) {
                      height = Math.round((height * MAX_SIZE) / width);
                      width = MAX_SIZE;
                    } else {
                      width = Math.round((width * MAX_SIZE) / height);
                      height = MAX_SIZE;
                    }
                  }

                  canvas.width = width;
                  canvas.height = height;
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    ctx.drawImage(img, 0, 0, width, height);
                    canvas.toBlob((b) => {
                      if (b) resolve(b);
                      else reject(new Error('Failed to create blob'));
                    }, 'image/jpeg', 0.85);
                  } else {
                    reject(new Error('Failed to get canvas context'));
                  }
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = URL.createObjectURL(blob);
              });

              // Save both to cache
              await saveImageToDB(currentImage.path, blob); // Save Original
              await saveImageToDB(currentImage.path + '_preview', resizedBlob); // Save Preview

              srcToLoad = URL.createObjectURL(resizedBlob);
            }
          } catch (err) {
            console.error('[SmartCache] CRITICAL ERROR - Preview creation failed:', err);
            // DO NOT fallback to original - show error instead
            if (isMounted) {
              setError('Failed to create preview. Please try again.');
              setIsLoading(false);
            }
            return;
          }
        } else {
          // Fallback for unknown
          srcToLoad = currentImage.url || '';
        }

        if (!isMounted) return;

        // Load the preview image
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          if (isMounted) {
            setImgObj(img);
            setIsLoading(false);
            fitImageToContainer(img);
          }
        };
        img.onerror = (e) => {
          if (isMounted) {
            console.error('Image load error:', e);
            setError('Failed to load preview image');
            setIsLoading(false);
          }
        };
        img.src = srcToLoad;

      } catch (e: any) {
        if (isMounted) {
          setError('Failed to process image: ' + e.message);
          setIsLoading(false);
        }
      }
    };

    // If HD explicitly clicked, load HD version from cache
    if (isHD && hdUrl) {
      const img = new window.Image();
      img.src = hdUrl;
      img.onload = () => {
        if (isMounted) {
          setImgObj(img);
          setIsLoading(false);
          fitImageToContainer(img);
        }
      };
      img.onerror = () => {
        if (isMounted) {
          setError('Failed to load HD version');
          setIsLoading(false);
        }
      };
      return () => { isMounted = false; };
    }

    loadSmartImage();

    return () => { isMounted = false; };
  }, [currentImage]); // Removed isHD and hdUrl dependencies - HD is loaded manually
  // Fit Image logic
  const fitImageToContainer = (img: HTMLImageElement) => {
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    const scaleX = containerWidth / img.width;
    const scaleY = containerHeight / img.height;
    const newScale = Math.min(scaleX, scaleY) * 0.9; // 90% fit

    setScale(newScale);
    setPosition({
      x: (containerWidth - img.width * newScale) / 2,
      y: (containerHeight - img.height * newScale) / 2
    });
    setStageSize({ width: containerWidth, height: containerHeight });
  };

  // Resize handler
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current && imgObj) {
        setStageSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imgObj]);

  // Events
  const handleWheel = (e: any) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    const oldScale = scale;
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - position.x) / oldScale,
      y: (pointer.y - position.y) / oldScale,
    };

    const newScale = e.evt.deltaY < 0 ? oldScale * 1.1 : oldScale / 1.1;

    // Limit zoom
    if (newScale < 0.1 || newScale > 10) return;

    setScale(newScale);
    setPosition({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    });
  };

  const handleMouseDown = (e: any) => {
    if (tool === 'move') return; // Let Konva handle dragging

    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    const x = (pos.x - position.x) / scale;
    const y = (pos.y - position.y) / scale;

    if (tool === 'polygon') {
      if (!newAnnotation) {
        // Poligon başlangıcı
        setIsDrawing(true);
        setNewAnnotation({
          x, y, width: 0, height: 0,
          shape: 'polygon',
          color: getRandomColor(),
          points: [{ x, y }, { x, y }] // İlk nokta sabit, ikinci nokta hareketli
        });
      } else {
        // Yeni nokta ekle
        const points = [...(newAnnotation.points || [])];
        // Son (hareketli) noktayı sabitle
        points[points.length - 1] = { x, y };
        // Yeni hareketli nokta ekle
        points.push({ x, y });
        setNewAnnotation({ ...newAnnotation, points });
      }
    } else {
      // Rect ve Circle
      setIsDrawing(true);
      setNewAnnotation({
        x, y, width: 0, height: 0,
        shape: tool === 'circle' ? 'circle' : 'rectangle',
        color: getRandomColor()
      });
    }
  };

  const handleMouseMove = (e: any) => {
    if (!isDrawing || !newAnnotation) return;

    const stage = e.target.getStage();
    const pos = stage.getPointerPosition();
    const x = (pos.x - position.x) / scale;
    const y = (pos.y - position.y) / scale;

    if (tool === 'polygon') {
      const points = [...(newAnnotation.points || [])];
      if (points.length > 0) {
        points[points.length - 1] = { x, y };
        setNewAnnotation({ ...newAnnotation, points });
      }
    } else {
      setNewAnnotation({
        ...newAnnotation,
        width: x - (newAnnotation.x || 0),
        height: y - (newAnnotation.y || 0)
      });
    }
  };

  const handleMouseUp = () => {
    if (tool === 'polygon') return; // Poligon, çift tıklama ile biter

    if (!isDrawing || !newAnnotation) return;
    setIsDrawing(false);

    // Normalize geometry (handle negative width/height)
    let { x, y, width, height } = newAnnotation;
    if (width! < 0) { x = (x || 0) + width!; width = Math.abs(width!); }
    if (height! < 0) { y = (y || 0) + height!; height = Math.abs(height!); }

    // Ignore tiny shapes
    if (width! < 5 || height! < 5) {
      setNewAnnotation(null);
      return;
    }

    const annotation: Annotation = {
      id: uuidv4(),
      imageData: currentImage!,
      x: x!, y: y!, width: width!, height: height!,
      color: newAnnotation.color || 'red',
      shape: newAnnotation.shape as AnnotationShape,
      comment: '',
      severity: 3
    };

    onAddAnnotation(annotation);
    setNewAnnotation(null);
    setSelectedAnnotationId(annotation.id);

    setTool('move'); // Switch back to move after drawing
  };

  // ... inside ImageAnnotator component

  // Fix: handleSaveAnnotation was incorrectly placed inside handleMouseUp.
  // We need to define it at the component level to be used by the Annotation Card.

  // Note: The annotation card logic is currently inline in the JSX (lines 938+).
  // We should extract the save logic into a function that the button helper calls, 
  // OR update the inline JSX to call this new function.

  // However, looking at the code structure, the "Annotation Card" (lines 939+) doesn't have a "Save" button 
  // It has inputs that call onUpdateAnnotation directly on change.
  // BUT the user request implies we want an explicit "Save" action that persists to DB?
  // OR we persist on every change?

  // Let's create a specific function for "Save to Database" and add a button to the card.

  const handleSaveToDatabase = async (ann: Annotation) => {
    const currentImage = images[currentImageIndex];
    // Use state values or passed annotation
    const tempSeverity = ann.severity || 3;

    try {
      if (currentImage) {
        const record: InspectionRecord = {
          id: ann.id,
          projectId: projectId || 'unknown', // Use real project ID from parent
          windFarmName: 'Demo Wind Farm',
          turbineName: currentImage.turbineName || 'Unknown',
          bladeName: currentImage.bladeName || 'Unknown',
          surfaceType: currentImage.surfaceType || 'Unknown',
          damageType: ann.damageType || 'detected',
          severity: tempSeverity,
          description: ann.description || ann.comment || '',
          comment: ann.comment,
          imageUrl: currentImage.url,
          status: 'detected',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        await inspectionService.save(record);
        alert('Saved to inspections database!');
      }
    } catch (err) {
      console.error('Failed to save to DB:', err);
      alert('Failed to save to database.');
    }
  };

  const handleDblClick = (e: any) => {
    e.evt.preventDefault();
    e.evt.stopPropagation();

    if (tool !== 'polygon' || !newAnnotation || !newAnnotation.points) return;

    setIsDrawing(false);

    // Son eklenen (hareketli) noktayı kaldır
    const points = newAnnotation.points.slice(0, -1);

    if (points.length < 3) {
      setNewAnnotation(null); // Yeterli nokta yok
      return;
    }

    // Bounding box hesapla
    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const annotation: Annotation = {
      id: uuidv4(),
      imageData: currentImage!,
      x: minX, y: minY,
      width: maxX - minX,
      height: maxY - minY,
      color: newAnnotation.color || 'red',
      shape: 'polygon',
      comment: '',
      severity: 3,
      points: points
    };

    onAddAnnotation(annotation);
    setNewAnnotation(null);
    setSelectedAnnotationId(annotation.id);
    setTool('move');
  };

  const getRandomColor = () => {
    const colors = ['#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // Render
  if (!currentImage) {
    return (
      <MainContainer>
        <div style={{ margin: 'auto', color: '#6b7280' }}>No image selected</div>
      </MainContainer>
    );
  }

  const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId);

  return (
    <MainContainer>
      {/* Left Toolbar */}
      <ToolbarContainer>
        <ToolGroup>
          <ToolButton active={tool === 'move'} onClick={() => setTool('move')} title="Move (Pan)">
            <Move />
          </ToolButton>
          <ToolButton onClick={() => {
            const newScale = scale * 1.2;
            setScale(newScale);
            // Center zoom logic could be added here
          }} title="Zoom In">
            <ZoomIn />
          </ToolButton>
          <ToolButton onClick={() => {
            const newScale = Math.max(0.1, scale / 1.2);
            setScale(newScale);
          }} title="Zoom Out">
            <ZoomOut />
          </ToolButton>
        </ToolGroup>

        <ToolGroup>
          <ToolButton active={tool === 'rect'} onClick={() => setTool('rect')} title="Rectangle">
            <Square />
          </ToolButton>
          <ToolButton active={tool === 'circle'} onClick={() => setTool('circle')} title="Circle">
            <CircleIcon />
          </ToolButton>
          <ToolButton active={tool === 'polygon'} onClick={() => setTool('polygon')} title="Polygon">
            <PenTool />
          </ToolButton>
        </ToolGroup>

        <ToolGroup>
          <ToolButton onClick={async () => {
            if (selectedAnnotation && imgObj) {
              setIsAiAnalyzing(true);
              try {
                console.log('[AI] Starting analysis for annotation:', selectedAnnotation.id);

                // 1. Capture the area
                const imageBase64 = captureAnnotationArea(
                  stageRef.current?.getStage(),
                  imgObj,
                  {
                    x: selectedAnnotation.x,
                    y: selectedAnnotation.y,
                    width: selectedAnnotation.width,
                    height: selectedAnnotation.height
                  },
                  scale
                );

                console.log('[AI] Image captured, base64 length:', imageBase64?.length);

                // 2. Analyze
                const result = await analyzeBladeDamage(
                  imageBase64,
                  {
                    x: selectedAnnotation.x,
                    y: selectedAnnotation.y,
                    width: selectedAnnotation.width,
                    height: selectedAnnotation.height
                  },
                  currentImage.turbineName,
                  currentImage.bladeName,
                  currentImage.surfaceType
                );

                console.log('[AI] Analysis result:', result);

                onUpdateAnnotation({
                  ...selectedAnnotation,
                  ...result,
                  damageType: result.damageType as DamageType,
                  severity: result.severity || 3,
                  aiAnalyzed: true
                });
              } catch (e) {
                console.error('[AI] Analysis Failed:', e);
                alert('AI analysis failed. Check console for details.');
              } finally {
                setIsAiAnalyzing(false);
              }
            }
          }} disabled={!selectedAnnotation || isAiAnalyzing} title="AI Analyze">
            <Wand2 />
          </ToolButton>
        </ToolGroup>
      </ToolbarContainer>

      {/* Center Canvas */}
      <CanvasArea ref={containerRef}>
        <NavigationBar>
          <NavButton onClick={() => setCurrentImageIndex(Math.max(0, currentImageIndex - 1))} disabled={currentImageIndex === 0}>
            <ChevronLeft size={20} />
          </NavButton>
          <NavText>Image {filteredImages.findIndex(img => img.path === currentImage.path) + 1} / {filteredImages.length}</NavText>
          <NavButton onClick={() => setCurrentImageIndex(Math.min(filteredImages.length - 1, currentImageIndex + 1))} disabled={currentImageIndex === filteredImages.length - 1}>
            <ChevronRight size={20} />
          </NavButton>
        </NavigationBar>

        {isLoading && <LoadingSpinner style={{ position: 'absolute', zIndex: 20 }} />}
        {error && <ErrorMessage>{error}</ErrorMessage>}

        {altitudeOffset !== null && (
          <div style={{
            position: 'absolute',
            top: '4rem',
            right: '1rem',
            background: 'rgba(0, 0, 0, 0.75)',
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            fontWeight: 600,
            fontSize: '1.125rem',
            zIndex: 100,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}>
            {altitudeOffset >= 0 ? '+' : ''}{altitudeOffset}m
          </div>
        )}

        {/* HD Toggle Button */}
        <button
          onClick={async () => {
            if (isHD) {
              // Switch back to preview
              setIsHD(false);
            } else {
              // Switch to HD
              if (!hdUrl) {
                try {
                  // Fetch original blob from DB (key is just the path)
                  const { getImageFromDB } = await import('../utils/zipUtils');
                  const blob = await getImageFromDB(currentImage.path);
                  if (blob) {
                    const url = URL.createObjectURL(blob);
                    setHdUrl(url);
                    setIsHD(true);
                    // Update Konva Image safely
                    const fullImg = new window.Image();
                    fullImg.src = url;
                    fullImg.onload = () => {
                      setImgObj(fullImg);
                    };
                  } else {
                    alert('Original HD image not found.');
                  }
                } catch (e) {
                  console.error('Failed to load HD image:', e);
                }
              } else {
                setIsHD(true);
                const fullImg = new window.Image();
                fullImg.src = hdUrl;
                fullImg.onload = () => {
                  setImgObj(fullImg);
                };
              }
            }
          }}
          style={{
            position: 'absolute',
            top: '1rem',
            left: '1rem',
            background: isHD ? '#2563eb' : 'rgba(255, 255, 255, 0.9)',
            color: isHD ? 'white' : '#374151',
            padding: '0.4rem 0.8rem',
            borderRadius: '6px',
            fontWeight: 700,
            fontSize: '0.8rem',
            zIndex: 100,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: isHD ? 'none' : '1px solid #d1d5db',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            transition: 'all 0.2s'
          }}
          title={isHD ? "Show Preview" : "Load Original Full Quality"}
        >
          {isHD ? 'HD ON' : 'HD OFF'}
        </button>

        <CanvasWrapper>
          <Stage
            width={stageSize.width}
            height={stageSize.height}
            draggable={tool === 'move'}
            onWheel={handleWheel}
            scaleX={scale}
            scaleY={scale}
            x={position.x}
            y={position.y}
            onDragEnd={(e) => setPosition({ x: e.target.x(), y: e.target.y() })}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onDblClick={handleDblClick}
            ref={stageRef}
          >
            <Layer ref={imageLayerRef}>
              {imgObj && (
                <KonvaImage image={imgObj} />
              )}
              {currentAnnotations.map((ann) => (
                <Group key={ann.id}
                  draggable={tool === 'move'}
                  onClick={() => setSelectedAnnotationId(ann.id)}
                >
                  {ann.shape === 'polygon' && ann.points ? (
                    <Line
                      points={ann.points.flatMap(p => [p.x, p.y])}
                      closed={true}
                      stroke={ann.color}
                      strokeWidth={2 / scale}
                      fill={selectedAnnotationId === ann.id ? `${ann.color}33` : 'transparent'}
                    />
                  ) : ann.shape === 'circle' ? (
                    <KonvaCircle
                      x={ann.x + ann.width / 2}
                      y={ann.y + ann.height / 2}
                      radius={Math.max(ann.width, ann.height) / 2}
                      stroke={ann.color}
                      strokeWidth={2 / scale}
                      fill={selectedAnnotationId === ann.id ? `${ann.color}33` : 'transparent'}
                    />
                  ) : (
                    <Rect
                      x={ann.x}
                      y={ann.y}
                      width={ann.width}
                      height={ann.height}
                      stroke={ann.color}
                      strokeWidth={2 / scale}
                      fill={selectedAnnotationId === ann.id ? `${ann.color}33` : 'transparent'}
                    />
                  )}
                </Group>
              ))}
              {newAnnotation && (
                <>
                  {newAnnotation.shape === 'polygon' && newAnnotation.points ? (
                    <Line
                      points={newAnnotation.points.flatMap(p => [p.x, p.y])}
                      closed={false}
                      stroke={newAnnotation.color}
                      strokeWidth={2 / scale}
                    />
                  ) : (
                    <Rect
                      x={newAnnotation.x}
                      y={newAnnotation.y}
                      width={newAnnotation.width}
                      height={newAnnotation.height}
                      stroke={newAnnotation.color}
                      strokeWidth={2 / scale}
                    />
                  )}
                </>
              )}
            </Layer>
          </Stage>
        </CanvasWrapper>
      </CanvasArea>

      {/* Right Info Panel */}
      <RightPanel>
        <PanelHeader>
          <h2>Inspection Details</h2>
          <p>{currentImage.bladeName} - {currentImage.surfaceType}</p>
        </PanelHeader>
        <PanelContent>
          <InfoRow>
            <span>Turbine:</span>
            <span>{currentImage.turbineName}</span>
          </InfoRow>
          <InfoRow>
            <span>Blade:</span>
            <span>{currentImage.bladeName}</span>
          </InfoRow>
          <InfoRow>
            <span>Side:</span>
            <span>{currentImage.surfaceType}</span>
          </InfoRow>
          <InfoRow>
            <span>Image:</span>
            <span style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentImage.fileName}</span>
          </InfoRow>

          {selectedAnnotation && (
            <AnnotationCard>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <strong>Damage #{selectedAnnotation.id.slice(0, 4)}</strong>
                <button onClick={() => {
                  onDeleteAnnotation(selectedAnnotation.id);
                  setSelectedAnnotationId(null);
                }} style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }}>
                  <Trash2 size={16} />
                </button>
              </div>

              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Damage Type</label>
                <select
                  style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  value={selectedAnnotation.damageType || ''}
                  onChange={(e) => onUpdateAnnotation({ ...selectedAnnotation, damageType: e.target.value as DamageType })}
                >
                  <option value="">Select Type</option>
                  {Object.values(DamageType).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Severity (1-5)</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  value={selectedAnnotation.severity || 3}
                  onChange={(e) => onUpdateAnnotation({ ...selectedAnnotation, severity: Math.min(5, Math.max(1, parseInt(e.target.value) || 3)) })}
                />
              </div>

              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ fontSize: '0.75rem', color: '#6b7280', display: 'block', marginBottom: '0.25rem' }}>Comment</label>
                <textarea
                  style={{ width: '100%', padding: '0.25rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
                  rows={3}
                  value={selectedAnnotation.comment}
                  onChange={(e) => onUpdateAnnotation({ ...selectedAnnotation, comment: e.target.value })}
                />
              </div>

              {/* ... inside AnnotationCard ... */}

              <div style={{ marginTop: '1rem', display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleSaveToDatabase(selectedAnnotation)}
                  style={{
                    flex: 1,
                    background: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Database size={14} />
                  Save to Database
                </button>
              </div>

              {selectedAnnotation.aiAnalyzed && (
                <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f0fdf4', borderRadius: '4px', fontSize: '0.75rem' }}>
                  {/* ... rest of AI analysis ... */}
                  <div style={{ fontWeight: 'bold', color: '#166534', marginBottom: '0.25rem' }}>AI Analysis</div>
                  <div><strong>Severity:</strong> {selectedAnnotation.severity || 3}/5</div>
                  <div><strong>Confidence:</strong> {(selectedAnnotation.confidence || 0).toFixed(1)}%</div>
                  <div style={{ marginTop: '0.25rem' }}><strong>Findings:</strong> {selectedAnnotation.description}</div>
                  {selectedAnnotation.suggestions && (
                    <div style={{ marginTop: '0.25rem', borderTop: '1px solid #bbf7d0', paddingTop: '0.25rem' }}>
                      <strong>Suggestion:</strong> {selectedAnnotation.suggestions}
                    </div>
                  )}
                </div>
              )}
            </AnnotationCard>
          )}

          {isAiAnalyzing && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: '#eff6ff', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e40af', marginBottom: '0.5rem' }}>🤖 AI Analyzing...</div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Processing damage image</div>
            </div>
          )}
        </PanelContent>
      </RightPanel>
    </MainContainer>
  );
};

export default ImageAnnotator;